import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { generateResponsiveFontStyles, toUcWords } from "@/lib/utils";
import { TruncatedTextWithTooltip } from "@/components/ui/truncated-text-with-tooltip";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Settings,
  Filter,
  X,
} from "lucide-react";
import { useTableConfig } from "@/hooks/useTableConfig";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { isComparisonEnabled } from "@/features/filters/filter.utils";
import type { TableFilterDTO, TableQueryDTO } from "@/types/table-query";
import {
  AtualizandoConteudo,
  CarregandoConteudo,
} from "@/components/ui/carregando-conteudo";

/* eslint-disable react-refresh/only-export-components */

// toUcWords agora vem centralizado de '@/lib/utils'
// Interface para colunas da tabela
export interface TableColumn {
  key: string;
  header: string;
  align?: "left" | "center" | "right";
  sortable?: boolean;
  /**
   * Tipo semântico para alinhar e dimensionar colunas automaticamente.
   * - **date**: esquerda
   * - **text**: esquerda
   * - **number/percent/currency**: direita
   *
   * Se não for definido, o `StandardizedTable` tenta inferir pelo conteúdo.
   */
  type?: "text" | "number" | "date" | "percent" | "currency";
  /**
   * Controle opcional de largura (para resolver discrepâncias visuais entre header e células).
   * Aceita número (px) ou string CSS (ex: '12rem', '160px').
   */
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  heatmapColor?: "green" | "red";
  hasDelta?: boolean;
  deltaType?: "growth" | "decline";
  visible?: boolean;
  alwaysVisible?: boolean; // Se true, a coluna não pode ser ocultada
  render?: (
    value: TableCellValue,
    item: TableRow,
    index: number,
    isTotal?: boolean,
  ) => React.ReactNode;
}

type TableCellValue = ReturnType<typeof JSON.parse>;

// Interface para dados da linha
export interface TableRow {
  [key: string]: TableCellValue;
  // Propriedades opcionais para delta dinâmico
  delta?:
    | {
        [key: string]: "growth" | "decline";
      }
    | number
    | string
    | null;
}

// Interface para linha de total
export interface TableTotal {
  [key: string]: TableCellValue;
}

// Interface para botão opcional
interface TableButton {
  text: string;
  path: string;
}

// Interface para filtros (DTO compartilhado com API)
type TableFilter = TableFilterDTO;

// Operadores de filtro disponíveis
const FILTER_OPERATORS = [
  { value: "equals", label: "Igual a" },
  { value: "contains", label: "Contém" },
  { value: "startsWith", label: "Começa com" },
  { value: "regex", label: "Regex" },
  { value: "in", label: "Em" },
] as const;

// Interface para props do componente
interface StandardizedTableProps {
  title: string | React.ReactNode;
  description?: string;
  /**
   * Classe opcional para customizar o fundo do header.
   * Ex.: 'bg-muted/50'
   */
  bg?: string;
  /**
   * Modo de renderização:
   * - **card**: renderiza Card + header (padrão do sistema)
   * - **plain**: renderiza apenas o conteúdo da tabela (útil para embeds)
   */
  variant?: "card" | "plain";
  /**
   * Se false, oculta o header (título/descrição + toolbar).
   * Útil para tabelas embutidas em outros componentes.
   */
  showHeader?: boolean;
  columns: TableColumn[];
  data: TableRow[];
  total?: TableTotal;
  /**
   * Como esta tabela totaliza, quando o rodapé automático não serve.
   *
   * O automático soma toda coluna numérica e tira a média de toda percentual, o que está
   * certo para métrica aditiva e errado para razão — somar ROAS linha a linha não dá ROAS
   * nenhum, e a média de duas participações complementares dá 50% sempre. Uma tabela cujo
   * rodapé precisa de outra conta passa esta função, que recebe as linhas **já filtradas**
   * (a busca e os filtros da própria tabela rodam antes) e devolve a linha de total, ou
   * `undefined` para não desenhar rodapé nenhum.
   *
   * `total` continua tendo precedência: quem já recebe o rodapé pronto do servidor o passa
   * de lá, e é ele que vale.
   */
  computeTotal?: (rows: TableRow[]) => TableTotal | undefined;
  pagination?: {
    current: number; // página atual (1-n)
    pageSize: number; // tamanho da página
    total: number; // total de registros
    onPageChange?: (page: number) => void; // callback para paginação server-side
  };
  /**
   * Se `server`, o componente apenas emite mudanças de query (busca/filtros/ordenação)
   * e espera que o caller faça o fetch no servidor (antes de paginar).
   */
  queryMode?: "client" | "server";
  onQueryChange?: (query: TableQueryDTO) => void;
  /**
   * Ordenação padrão ao carregar (override da detecção automática de receita).
   * Útil quando a ordem natural da tabela não é pela coluna de receita.
   */
  defaultSortBy?: string;
  defaultSortDir?: "asc" | "desc";
  button?: TableButton;
  className?: string;
  /** Indicador de carregamento */
  loading?: boolean;
  /** Texto da tabela carregada sem linhas. O padrão fala de período, que nem toda tabela tem. */
  mensagemVazia?: string;
  /**
   * Callback quando uma linha é clicada
   * @param row - Dados da linha clicada
   * @param index - Índice da linha na página atual
   * @param event - Evento de mouse (para detectar Ctrl+click)
   */
  onRowClick?: (row: TableRow, index: number, event?: React.MouseEvent) => void;
  /**
   * Índice da linha selecionada (para highlight visual)
   * Pode ser o índice na página atual ou uma função que verifica se a linha está selecionada
   */
  selectedRowIndex?: number | ((row: TableRow, index: number) => boolean);
  /**
   * Se true, linhas são clicáveis mesmo sem onRowClick
   * (útil para drill-down tables)
   */
  clickableRows?: boolean;
  /** Se true, oculta a linha de Total Geral (auto-calculada ou explícita). */
  hideTotals?: boolean;
  /** Classe opcional aplicada apenas ao espaço remanescente do card quando ele é esticado em altura. */
  fillRemainingSpaceClassName?: string;
}

import {
  formatCurrency as formatCurrencyUtil,
  formatInteger,
  formatDecimal,
  formatSignedPercent,
} from "@/utils/numberFormat";

// Função helper para renderizar indicador de tendência
export const TrendIndicator = ({
  value,
  showSymbol = true,
  enabled,
}: {
  value: number | undefined | null;
  showSymbol?: boolean;
  enabled?: boolean;
}) => {
  const { filter } = useGlobalFilter();
  const canShow = enabled ?? isComparisonEnabled(filter);
  if (!canShow) {
    return null;
  }

  if (value === null || value === undefined || isNaN(Number(value))) {
    return null;
  }

  // Exibir 0% explicitamente (evita colunas "vazias" quando não há variação)
  if (Number(value) === 0) {
    return <span className="text-muted-foreground text-xs leading-4">0%</span>;
  }

  const numValue = Number(value);
  const isPositive = numValue >= 0;
  const color = isPositive ? "text-success-soft" : "text-error-soft";

  // Uma casa decimal para caber nas tabelas densas; o sinal vai no número (item 34).
  const formattedValue = formatSignedPercent(numValue, 1);

  return (
    <span
      className={`inline-flex items-center gap-0.5 whitespace-nowrap text-xs leading-4 ${color}`}
    >
      {showSymbol ? (
        isPositive ? (
          <ArrowUp className="h-2.5 w-2.5" />
        ) : (
          <ArrowDown className="h-2.5 w-2.5" />
        )
      ) : null}
      <span>{formattedValue}</span>
    </span>
  );
};

/**
 * Renderiza "valor principal + comparação (%Δ)".
 * - layout='inline': lado a lado (padrão, ideal para tabelas)
 * - layout='stacked': empilhado verticalmente (ideal para cards KPI)
 */
export const ValueWithComparison = ({
  value,
  changePercentage,
  align = "right",
  showSymbol = true,
  enabled,
  layout = "inline",
}: {
  value: React.ReactNode;
  changePercentage: number | undefined | null;
  align?: "left" | "right" | "center";
  showSymbol?: boolean;
  enabled?: boolean;
  layout?: "inline" | "stacked";
}) => {
  const { filter } = useGlobalFilter();
  const canShow = enabled ?? isComparisonEnabled(filter);
  const justify =
    align === "right"
      ? "justify-end"
      : align === "center"
        ? "justify-center"
        : "justify-start";
  const items =
    align === "center"
      ? "items-center"
      : align === "right"
        ? "items-end"
        : "items-start";

  if (!canShow) {
    // Importante: mesmo sem comparação, manter o alinhamento semântico da coluna
    // (ex.: números/percentuais/moedas à direita).
    return <span className={`inline-flex w-full ${justify}`}>{value}</span>;
  }

  if (
    changePercentage === null ||
    changePercentage === undefined ||
    isNaN(Number(changePercentage))
  ) {
    return <span className={`inline-flex w-full ${justify}`}>{value}</span>;
  }

  const trend = (
    <TrendIndicator
      value={changePercentage}
      showSymbol={showSymbol}
      enabled={canShow}
    />
  );

  // Layout empilhado para cards KPI
  if (layout === "stacked") {
    return (
      <div className={`flex flex-col w-full ${items}`}>
        <span>{value}</span>
        <span className="whitespace-nowrap text-[11px] leading-none mt-1">
          {trend}
        </span>
      </div>
    );
  }

  // Layout inline (padrão) para tabelas - compacto para caber em colunas estreitas
  return (
    <span className={`inline-flex w-full items-baseline gap-1 ${justify}`}>
      <span className="flex-shrink-0">{value}</span>
      <span className="whitespace-nowrap leading-none flex-shrink-0">
        {trend}
      </span>
    </span>
  );
};

// Função helper para formatar moeda (usando utilitário padrão)
export const formatCurrency = (value: number | undefined | null): string => {
  return formatCurrencyUtil(value);
};

// Função helper para formatar número (usando utilitário padrão)
export const formatNumber = (value: number | undefined | null): string => {
  return formatInteger(value);
};

// Tracker global: evita re-emissão da query inicial em tabelas server-side
// quando o componente é desmontado/remontado durante loading (previne loops).
const _serverInitializedTables = new Set<string>();

// Componente principal da tabela padronizada
export const StandardizedTable: React.FC<StandardizedTableProps> = ({
  bg,
  title,
  description,
  variant = "card",
  showHeader = true,
  columns,
  data,
  total,
  computeTotal,
  pagination,
  queryMode = "client",
  onQueryChange,
  defaultSortBy,
  defaultSortDir,
  button,
  className = "",
  loading,
  mensagemVazia = "Nenhum dado encontrado para o período selecionado.",
  onRowClick,
  selectedRowIndex,
  clickableRows = false,
  hideTotals = false,
  fillRemainingSpaceClassName,
}) => {
  const navigate = useNavigate();
  const { filter } = useGlobalFilter();
  const comparisonEnabled = isComparisonEnabled(filter);
  const {
    visibleColumns,
    sortConfig,
    toggleColumnVisibility,
    toggleAllColumns,
    handleSort,
    tableId,
  } = useTableConfig(title, columns, {
    defaultSortKey: defaultSortBy,
    defaultSortDirection: defaultSortDir,
  });

  // Chave estável para rastrear inicialização server-side (previne loops)
  const _tableInitKey = tableId;

  // Cleanup: ao desmontar permanentemente (navegação), permitir re-emissão futura.
  // Delay de 2s distingue desmontagem por loading (rápida) de navegação (permanente).
  useEffect(() => {
    return () => {
      const key = _tableInitKey;
      window.setTimeout(() => {
        _serverInitializedTables.delete(key);
      }, 2000);
    };
  }, [_tableInitKey]);

  // Estado para os filtros
  const [filters, setFilters] = useState<TableFilter[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const filtersPanelRef = useRef<HTMLDivElement>(null);
  const [clientPage, setClientPage] = useState(1);
  const [jaCarregou, setJaCarregou] = useState(false);
  // Ajuste de estado durante a renderização (padrão do React para estado derivado de props).
  if ((loading || data.length > 0) && !jaCarregou) setJaCarregou(true);
  const [globalSearch, setGlobalSearch] = useState("");
  const isServerQueryMode = queryMode === "server" && !!pagination;
  const lastEmittedQueryKeyRef = useRef<string | null>(null);
  const didInitializeServerQueryRef = useRef(false);

  // Emitir query (server-side) com debounce
  useEffect(() => {
    if (!onQueryChange) return;
    if (!isServerQueryMode) return;

    const activeFilters = filters.filter(
      (f) => f.value.trim() !== "" && f.columnKey,
    );
    const payload: TableQueryDTO = {
      q: globalSearch.trim() || undefined,
      sortBy: sortConfig?.key,
      sortDir: sortConfig?.direction,
      filters: activeFilters.length ? activeFilters : undefined,
    };

    // Evita loop infinito: não emite a mesma query repetidamente.
    // (O callback normalmente é inline no caller e muda a cada render.)
    const payloadKey = JSON.stringify(payload);

    // Na inicialização, emitir query UMA VEZ se houver ordenação padrão.
    // Isso garante que tabelas server-side busquem dados já ordenados.
    // Para evitar loop (mount → fetch → unmount → remount → fetch), usamos
    // um Set global que rastreia tabelas já inicializadas nesta sessão.
    if (!didInitializeServerQueryRef.current) {
      didInitializeServerQueryRef.current = true;
      lastEmittedQueryKeyRef.current = payloadKey;

      if (payload.sortBy && !_serverInitializedTables.has(_tableInitKey)) {
        _serverInitializedTables.add(_tableInitKey);
        window.setTimeout(() => onQueryChange(payload), 50);
      }
      return;
    }

    if (payloadKey === lastEmittedQueryKeyRef.current) return;
    lastEmittedQueryKeyRef.current = payloadKey;

    const t = window.setTimeout(() => onQueryChange(payload), 350);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSearch, filters, sortConfig, isServerQueryMode, onQueryChange]);

  // Fechar filtros ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Verificar se o clique foi dentro do painel de filtros
      if (filtersPanelRef.current && filtersPanelRef.current.contains(target)) {
        return;
      }

      // Verificar se o clique foi dentro de um portal do Radix (Select, Popover, etc.)
      // Os portais do Radix usam data-radix-popper-content-wrapper ou são filhos de [data-radix-portal]
      const radixPortal = (target as Element).closest?.(
        '[data-radix-portal], [data-radix-popper-content-wrapper], [role="listbox"], [data-radix-select-viewport]',
      );
      if (radixPortal) {
        return;
      }

      setShowFilters(false);
    };

    if (showFilters) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilters]);

  const normalizeText = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    return String(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  };

  const parseRegex = (raw: string): RegExp | null => {
    const v = raw.trim();
    if (!v) return null;
    // Suporta /pattern/flags
    if (v.startsWith("/") && v.lastIndexOf("/") > 0) {
      const last = v.lastIndexOf("/");
      const pattern = v.slice(1, last);
      const flags = v.slice(last + 1) || "i";
      try {
        return new RegExp(pattern, flags.includes("i") ? flags : `${flags}i`);
      } catch {
        return null;
      }
    }
    try {
      return new RegExp(v, "i");
    } catch {
      return null;
    }
  };

  const splitInValues = (raw: string): string[] => {
    return raw
      .split(/[,;\n]/g)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  // Função para aplicar busca global + filtros por coluna
  const applyFilters = (rows: TableRow[]) => {
    const search = globalSearch.trim();
    const activeFilters = filters.filter(
      (f) => f.value.trim() !== "" && f.columnKey,
    );
    if (!search && activeFilters.length === 0) return rows;

    return rows.filter((item) => {
      // Busca global: procurar em todas as colunas
      if (search) {
        const needle = normalizeText(search);
        const hasMatch = columns.some((col) => {
          const hay = normalizeText(item[col.key]);
          return hay.includes(needle);
        });
        if (!hasMatch) return false;
      }

      return activeFilters.every((filter) => {
        const cellRaw = item[filter.columnKey];
        const cellText = normalizeText(cellRaw);
        const input = filter.value.trim();

        switch (filter.operator) {
          case "equals": {
            return cellText === normalizeText(input);
          }
          case "contains": {
            return cellText.includes(normalizeText(input));
          }
          case "startsWith": {
            return cellText.startsWith(normalizeText(input));
          }
          case "regex": {
            const rx = parseRegex(input);
            if (!rx) return false;
            return rx.test(String(cellRaw ?? ""));
          }
          case "in": {
            const values = splitInValues(input).map(normalizeText);
            if (values.length === 0) return true;
            return values.includes(cellText);
          }
          default:
            return true;
        }
      });
    });
  };

  // Função para adicionar um novo filtro
  const addFilter = () => {
    const newFilter: TableFilter = {
      columnKey: columns[0]?.key || "",
      operator: "contains",
      value: "",
    };
    setFilters([...filters, newFilter]);
  };

  // Função para remover um filtro
  const removeFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  // Função para atualizar um filtro
  const updateFilter = (
    index: number,
    field: keyof TableFilter,
    value: string,
  ) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], [field]: value };
    setFilters(newFilters);
  };

  // Função para limpar todos os filtros
  const clearAllFilters = () => {
    setFilters([]);
  };

  // Função para ordenar os dados
  const sortData = (data: TableRow[]) => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Verificar se os valores são números
      const aNum = Number(aValue);
      const bNum = Number(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum);

      if (isNumeric) {
        return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      // Para strings, converter para minúsculas para comparação
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aStr > bStr) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  // Função para obter o ícone de ordenação
  const getSortIcon = (key: string) => {
    const column = columns.find((col) => col.key === key);
    if (!column || column.sortable === false) return null;

    if (sortConfig?.key === key) {
      return sortConfig.direction === "asc" ? (
        <ArrowUp className="h-3 w-3 ml-1 opacity-60" />
      ) : (
        <ArrowDown className="h-3 w-3 ml-1 opacity-60" />
      );
    }
    return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
  };

  // Dados filtrados e ordenados
  const filteredData = useMemo(
    () => (isServerQueryMode ? data : applyFilters(data)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, filters, globalSearch, columns, isServerQueryMode],
  );
  const sortedData = isServerQueryMode ? data : sortData(filteredData);

  // Paginação:
  // - **server-side**: quando `pagination` é fornecido (mantém comportamento atual)
  // - **client-side (auto)**: quando NÃO há `pagination`, mas estamos no modo padrão (card + header)
  const shouldClientPaginate = !pagination && variant !== "plain" && showHeader;

  // Resetar página client-side ao mudar filtros/dados
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (shouldClientPaginate) setClientPage(1);
  }, [shouldClientPaginate, filters, globalSearch, data]);

  const pageSize = pagination?.pageSize ?? 10;
  const currentPage =
    pagination?.current ?? (shouldClientPaginate ? clientPage : 1);
  const totalCount = pagination?.total ?? sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / (pageSize || 1)));

  const paginatedData = (() => {
    if (pagination) return sortedData.slice(0, pageSize); // server-side: aplica sort/filtro/busca na página atual
    if (!shouldClientPaginate) return sortedData; // plain/embed: não paginar automaticamente
    const startIdx = (currentPage - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    return sortedData.slice(startIdx, endIdx);
  })();

  const showPagination = Boolean(pagination) || shouldClientPaginate;
  const handlePageChange = (next: number) => {
    const safe = Math.min(Math.max(1, next), totalPages);
    if (pagination?.onPageChange) {
      pagination.onPageChange(safe);
      return;
    }
    setClientPage(safe);
  };

  // Filtrar colunas visíveis
  // Regra 1: quando o filtro estiver "Sem comparação", não exibir colunas de comparação (% Δ)
  // Regra 2: colunas % Δ herdam visibilidade da coluna pai (a coluna imediatamente anterior)
  const visibleColumnsList = columns.filter((col, idx) => {
    const isDelta = (col.header || "").includes("Δ");
    if (isDelta) {
      if (!comparisonEnabled) return false;
      // Herda visibilidade da coluna pai (anterior não-delta)
      for (let i = idx - 1; i >= 0; i--) {
        if (!(columns[i].header || "").includes("Δ")) {
          return visibleColumns.has(columns[i].key);
        }
      }
      return false;
    }
    return visibleColumns.has(col.key);
  });

  // Função para calcular intensidade do mapa de calor
  const calculateHeatmapIntensity = (value: number, columnKey: string) => {
    const column = columns.find((col) => col.key === columnKey);
    // Agora suporta qualquer tipo de coluna (percent, number, currency) desde que tenha heatmapColor
    if (!column || !column.heatmapColor) return 0;

    // Encontrar valores mínimos e máximos para esta coluna
    const values = data
      .map((item) => {
        const val = item[columnKey];
        return typeof val === "number" ? val : parseFloat(val) || 0;
      })
      .filter((v) => !isNaN(v));

    if (values.length === 0) return 0;

    const min = Math.min(...values);
    const max = Math.max(...values);

    if (min === max) return 0.5; // Se todos os valores são iguais, usar intensidade média

    // Calcular intensidade baseada na posição do valor entre min e max
    return (value - min) / (max - min);
  };

  // Função para obter classes CSS do mapa de calor
  const getHeatmapClasses = (value: number, columnKey: string) => {
    const column = columns.find((col) => col.key === columnKey);
    // Agora suporta qualquer tipo de coluna desde que tenha heatmapColor definido
    if (!column || !column.heatmapColor) return "";

    const intensity = calculateHeatmapIntensity(value, columnKey);

    if (column.heatmapColor === "green") {
      // Gradiente verde: do mais escuro (intensidade 1) ao mais claro (intensidade 0)
      if (intensity >= 0.8) return "bg-green-800 text-white";
      if (intensity >= 0.6) return "bg-green-700 text-white";
      if (intensity >= 0.4) return "bg-green-600 text-white";
      if (intensity >= 0.2) return "bg-green-500 text-white";
      return "bg-green-400 text-white";
    } else if (column.heatmapColor === "red") {
      // Gradiente vermelho: do mais escuro (intensidade 1) ao mais claro (intensidade 0)
      if (intensity >= 0.8) return "bg-red-800 text-white";
      if (intensity >= 0.6) return "bg-red-700 text-white";
      if (intensity >= 0.4) return "bg-red-600 text-white";
      if (intensity >= 0.2) return "bg-red-500 text-white";
      return "bg-red-400 text-white";
    }

    return "";
  };

  // ===== Alinhamento + Largura (padronização global) =====
  const isPlaceholderValue = (value: unknown) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") {
      const v = value.trim();
      return v === "" || v.toUpperCase() === "N/D" || v === "—" || v === "-";
    }
    return false;
  };

  const isDateLike = (value: unknown) => {
    if (value instanceof Date && !isNaN(value.getTime())) return true;
    if (typeof value !== "string") return false;
    const v = value.trim();
    // yyyy-mm-dd ou yyyy-mm-ddThh:mm:ss
    if (/^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(v)) return true;
    // dd/mm/yyyy
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return true;
    return false;
  };

  const isNumericLike = (value: unknown) => {
    if (typeof value === "number") return Number.isFinite(value);
    if (typeof value !== "string") return false;
    const raw = value.trim();
    if (!raw) return false;
    if (raw.toUpperCase() === "N/D" || raw === "—" || raw === "-") return false;
    // Remover símbolos comuns (moeda/percentual) para inferência
    const cleaned = raw
      .replace(/\s/g, "")
      .replace(/^R\$/i, "")
      .replace(/%$/, "")
      .replace(/\./g, "")
      .replace(/,/g, ".");
    return /^-?\d+(\.\d+)?$/.test(cleaned);
  };

  const inferColumnType = (column: TableColumn): TableColumn["type"] => {
    if (column.type) return column.type;

    const key = (column.key || "").toLowerCase();
    const header = (column.header || "").toLowerCase();

    // Heurística forte para datas
    if (
      key.includes("date") ||
      key.includes("data") ||
      header.includes("data")
    ) {
      return "date";
    }

    const sample = (data || []).slice(0, 25);
    const sampleValues: unknown[] = [
      ...sample.map((row) => row?.[column.key]),
      ...(total ? [total?.[column.key]] : []),
    ];

    let numericCount = 0;
    let dateCount = 0;
    let textCount = 0;

    for (const v of sampleValues) {
      if (React.isValidElement(v)) continue;
      if (isPlaceholderValue(v)) continue;
      if (isDateLike(v)) {
        dateCount += 1;
        continue;
      }
      if (isNumericLike(v)) {
        numericCount += 1;
        continue;
      }
      textCount += 1;
    }

    if (dateCount >= 2 && dateCount >= numericCount && dateCount >= textCount)
      return "date";
    if (numericCount >= 2 && numericCount >= textCount) return "number";
    return "text";
  };

  const getResolvedAlignment = (
    column: TableColumn,
  ): "left" | "center" | "right" => {
    if (column.align) return column.align;
    const t = inferColumnType(column);
    if (t === "number" || t === "percent" || t === "currency") return "right";
    if (t === "date" || t === "text") return "left";
    return "left";
  };

  const toCssSize = (
    value: number | string | undefined,
  ): string | undefined => {
    if (value === undefined) return undefined;
    return typeof value === "number" ? `${value}px` : value;
  };

  const clamp = (n: number, min: number, max: number) =>
    Math.max(min, Math.min(max, n));

  const normalizeDateForMeasure = (value: string) => {
    const v = value.trim();
    // yyyy-mm-dd -> dd/mm/yyyy (mesma quantidade de chars, mas padroniza)
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return v;
  };

  const formatForMeasure = (
    value: unknown,
    type: TableColumn["type"],
  ): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") {
      if (type === "date") return normalizeDateForMeasure(value);
      return value.trim();
    }
    if (typeof value === "number") {
      if (!Number.isFinite(value)) return "";
      if (type === "currency") return formatCurrencyUtil(value);
      if (type === "percent") return `${formatDecimal(value, 2)}%`;
      // number/date/text fallback
      return formatInteger(value);
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
      const dd = String(value.getDate()).padStart(2, "0");
      const mm = String(value.getMonth() + 1).padStart(2, "0");
      const yyyy = String(value.getFullYear());
      return `${dd}/${mm}/${yyyy}`;
    }
    return String(value);
  };

  const measureMaxContentLen = (
    column: TableColumn,
    type: TableColumn["type"],
  ) => {
    const sample = (data || []).slice(0, 25);
    const sampleValues: unknown[] = [
      ...sample.map((row) => row?.[column.key]),
      ...(total ? [total?.[column.key]] : []),
    ];

    let maxLen = 0;
    for (const v of sampleValues) {
      // Se o valor já veio como ReactNode, não dá para medir com precisão.
      // Para colunas de data, usamos um fallback seguro (dd/mm/aaaa = 10 chars).
      if (React.isValidElement(v)) {
        if (type === "date") {
          maxLen = Math.max(maxLen, 10);
        }
        continue;
      }
      if (isPlaceholderValue(v)) continue;
      const measured = formatForMeasure(v, type);
      maxLen = Math.max(maxLen, measured.length);
    }
    return maxLen;
  };

  const isDeltaColumn = (column: TableColumn): boolean =>
    (column.header || "").includes("Δ");

  const getHeaderWidthCh = (
    column: TableColumn,
    index: number,
    type: TableColumn["type"],
  ) => {
    // Medida aproximada baseada no texto do título (em "ch").
    // É a forma mais simples/performática de fazer a coluna seguir o tamanho do header
    // e evitar scroll por colunas exageradas.
    const headerText = toUcWords(column.header || "").trim();
    const base = headerText.length;
    // espaço extra para ícone de sort + respiro do padding
    const slack = 2;
    const desired = base + slack;

    const t = type;
    const isFirst = index === 0;

    // Colunas delta (% Δ) são compactas mas precisam caber valores como "257,5%"
    if (isDeltaColumn(column)) {
      return clamp(desired, 7, 10);
    }

    // Quando comparação está ativa, colunas numéricas precisam de espaço extra
    // para acomodar o valor + indicador de comparação (ex: "R$ 36,5K +13,8%")
    const comparisonExtra = comparisonEnabled ? 6 : 0;

    // mínimos/máximos para manter legibilidade sem criar "espaço vazio"
    // Regras:
    // - date: geralmente curto (dd/mm/aaaa). Não deve herdar min alto da 1ª coluna.
    // - number/percent/currency: precisa de mais espaço quando há comparação.
    // - text (principalmente 1ª coluna): deve ser compacta para não dominar a tabela.
    let minCh: number;
    let maxCh: number;

    if (t === "date") {
      // Datas costumam precisar de 10+ chars (dd/mm/aaaa). Deixe um pouco de folga
      // para evitar corte visual por conta de fonte proporcional e padding.
      minCh = 11;
      maxCh = 14;
    } else if (t === "number" || t === "percent" || t === "currency") {
      // Colunas numéricas: precisam de mais espaço com comparação ativa
      minCh = 9 + comparisonExtra;
      maxCh = 14 + comparisonExtra;
    } else {
      // text: colunas de texto devem ser mais compactas para dar espaço às numéricas
      minCh = isFirst ? 10 : 7;
      maxCh = isFirst ? 18 : 14;
    }

    return clamp(desired, minCh, maxCh);
  };

  const getResolvedWidth = (
    column: TableColumn,
    index: number,
    totalVisible: number,
  ): string | undefined => {
    // Preferência: width explícita
    const explicit = toCssSize(column.width);
    if (explicit) return explicit;

    // Default: largura baseada no maior entre header e conteúdo (amostra), em "ch"
    const t = inferColumnType(column);
    const headerCh = getHeaderWidthCh(column, index, t);
    const contentLen = measureMaxContentLen(column, t);
    // slack extra pro conteúdo (separadores, % e etc.)
    const contentSlack = t === "date" ? 2 : 1;
    const contentCh = contentLen > 0 ? contentLen + contentSlack : 0;
    const widthCh = Math.max(headerCh, contentCh);
    return `${widthCh}ch`;
  };

  const resolvedColumnsMeta = useMemo(() => {
    const meta: Record<
      string,
      {
        align: "left" | "center" | "right";
        width?: string;
        minWidth?: string;
        maxWidth?: string;
      }
    > = {};
    visibleColumnsList.forEach((col, idx) => {
      meta[col.key] = {
        align: getResolvedAlignment(col),
        width: getResolvedWidth(col, idx, visibleColumnsList.length),
        minWidth: toCssSize(col.minWidth),
        maxWidth: toCssSize(col.maxWidth),
      };
    });
    return meta;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumnsList, data, total]);

  // ===== AUTO-COMPUTE TOTAL GERAL =====
  // Calcula automaticamente uma linha de total quando não fornecida explicitamente.
  // Tabelas que já fornecem `total` prop continuam usando o valor original.
  const autoComputedTotal = useMemo((): TableTotal | null => {
    // Em tabelas server-side paginadas, o componente recebe apenas a página atual.
    // Nesse modo, auto-somar geraria "Total Geral" parcial (da página), então desabilitamos.
    if (isServerQueryMode) return null;
    if (!sortedData || sortedData.length === 0) return null;
    // A tabela que sabe totalizar a si mesma decide aqui, sobre as linhas filtradas.
    if (computeTotal) return computeTotal(sortedData) ?? null;

    const result: TableTotal = {};
    let firstTextColumnSet = false;

    for (const column of columns) {
      const key = column.key;
      const type = inferColumnType(column);

      // Colunas de comparação (Δ / _change) ficam vazias no total
      if (key.includes("_change") || (column.header || "").includes("Δ")) {
        result[key] = "";
        continue;
      }

      // Primeira coluna de texto: label "Total Geral"
      if (type === "text" || type === "date") {
        if (!firstTextColumnSet) {
          result[key] = "Total Geral";
          firstTextColumnSet = true;
        } else {
          result[key] = "";
        }
        continue;
      }

      // Colunas numéricas: somar (number/currency) ou média (percent)
      let sum = 0;
      let count = 0;
      for (const row of sortedData) {
        const val = row[key];
        if (
          val === null ||
          val === undefined ||
          val === "" ||
          val === "N/D" ||
          val === "—"
        )
          continue;
        const num =
          typeof val === "number"
            ? val
            : parseFloat(
                String(val)
                  .replace(/[R$%\s]/g, "")
                  .replace(/\./g, "")
                  .replace(",", "."),
              );
        if (!isNaN(num) && isFinite(num)) {
          sum += num;
          count++;
        }
      }

      if (type === "percent") {
        result[key] = count > 0 ? sum / count : 0;
      } else {
        result[key] = sum;
      }
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedData, columns, isServerQueryMode, computeTotal]);

  // Total efetivo: usa o explícito se fornecido, senão auto-calcula
  const effectiveTotal = useMemo((): TableTotal | null => {
    if (hideTotals) return null;
    if (total && typeof total === "object" && Object.keys(total).length > 0) {
      // <M-230> A busca do próprio componente esconde o rodapé junto com as linhas.
      //
      // O rodapé que vem pronto descreve o conjunto do servidor, e não o que está na tela —
      // é por isso que ele sobrevive a uma página fora de faixa, onde `data` chega vazia. Mas
      // quando havia linhas e a busca **local** as escondeu todas, o que sobrava era um
      // "Total Geral" solto sob uma tabela sem nada, dizendo um número que não descreve
      // busca nenhuma. Os dois casos se distinguem exatamente aqui: `data` cheia e
      // `sortedData` vazia é a busca; `data` vazia é o conjunto.
      if (data.length > 0 && sortedData.length === 0) return null;
      return total;
    }
    return autoComputedTotal;
  }, [total, autoComputedTotal, hideTotals, data, sortedData]);

  // Função para renderizar o ícone de delta
  const renderDeltaIcon = (column: TableColumn, item: TableRow) => {
    if (!column.hasDelta) return null;

    // Verificar se existe delta dinâmico para esta coluna nesta linha
    const deltaValue =
      typeof item.delta === "object" && item.delta !== null
        ? item.delta[column.key]
        : undefined;
    if (!deltaValue) return null;

    const iconClass = "h-3 w-3 ml-1";

    if (deltaValue === "growth") {
      return <ArrowUp className={`${iconClass} text-green-600`} />;
    } else if (deltaValue === "decline") {
      return <ArrowDown className={`${iconClass} text-red-600`} />;
    }

    return null;
  };

  const header = showHeader ? (
    <CardHeader className={`overflow-visible ${bg}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 w-full relative">
        <div className="flex-1 min-w-[150px]">
          <CardTitle subtitle={description}>{title}</CardTitle>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 flex-shrink-0 max-w-full">
          {/* Busca global */}
          <div className="relative flex-shrink">
            <Input
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              placeholder="Buscar..."
              className="h-7 w-[120px] text-xs pr-7"
            />
            {globalSearch.trim() !== "" && (
              <button
                type="button"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setGlobalSearch("")}
                aria-label="Limpar busca"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Filtros */}
          <div className="relative flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={`h-7 px-1.5 text-muted-foreground hover:text-foreground ${filters.length > 0 ? "text-primary" : ""}`}
              title="Filtros"
            >
              <Filter className="h-3.5 w-3.5" />
              {filters.length > 0 && (
                <span className="ml-0.5 bg-primary text-primary-foreground text-[10px] rounded-full px-1 min-w-[14px] h-3.5 flex items-center justify-center">
                  {filters.length}
                </span>
              )}
            </Button>

            {showFilters && (
              <div
                ref={filtersPanelRef}
                className="absolute top-full right-0 mt-2 bg-background border border-border rounded-lg shadow-lg p-4 z-50 min-w-[400px]"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Filtros da Tabela</h4>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={addFilter}
                        className="h-6 px-2 text-xs"
                      >
                        + Adicionar
                      </Button>
                      {filters.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearAllFilters}
                          className="h-6 px-2 text-xs text-destructive"
                        >
                          Limpar
                        </Button>
                      )}
                    </div>
                  </div>

                  {filters.map((filter, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 bg-muted/50 rounded-md"
                    >
                      <div className="flex-1">
                        <Select
                          value={filter.columnKey}
                          onValueChange={(value) =>
                            updateFilter(index, "columnKey", value)
                          }
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Coluna" />
                          </SelectTrigger>
                          <SelectContent className="z-[60]">
                            {columns.map((column) => (
                              <SelectItem key={column.key} value={column.key}>
                                {toUcWords(column.header)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="w-32">
                        <Select
                          value={filter.operator}
                          onValueChange={(value) =>
                            updateFilter(index, "operator", value)
                          }
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[60]">
                            {FILTER_OPERATORS.map((op) => (
                              <SelectItem key={op.value} value={op.value}>
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex-1">
                        <Input
                          value={filter.value}
                          onChange={(e) =>
                            updateFilter(index, "value", e.target.value)
                          }
                          placeholder="Valor..."
                          className="h-8 text-xs"
                        />
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFilter(index)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}

                  {filters.length === 0 && (
                    <div className="text-center text-muted-foreground text-xs py-4">
                      Nenhum filtro aplicado. Clique em &quot;Adicionar&quot;
                      para criar um filtro.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {button && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(button.path)}
              className="h-7 px-2 text-xs font-medium"
            >
              {button.text}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                title="Configurações de colunas"
              >
                <Settings className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => toggleAllColumns(true)}>
                Mostrar todas as colunas
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => toggleAllColumns(false)}>
                Ocultar todas as colunas
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {columns
                .filter((col) => !(col.header || "").includes("Δ"))
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.key}
                    checked={visibleColumns.has(column.key)}
                    onCheckedChange={() => toggleColumnVisibility(column.key)}
                  >
                    {toUcWords(column.header)}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </CardHeader>
  ) : null;

  const semLinhas = paginatedData.length === 0;
  const carregandoComLinhas = !!loading && !semLinhas;
  // Busca ou filtro da própria tabela em uso. No modo servidor as linhas já chegam filtradas,
  // então `data` vazia não distingue "período sem dados" de "busca sem resultado" — isto sim.
  // Quem passa `loading` costuma disparar a busca num efeito, depois da primeira pintura, e
  // nela `loading` ainda é `false` com a tabela vazia. Dizer "nenhum dado" ali seria mentira:
  // o vazio só é afirmado depois de a tabela ter passado por um carregamento ou ter tido linhas.
  const vazioConfirmado = loading === undefined || jaCarregou;
  const buscaAtiva =
    globalSearch.trim() !== "" || filters.some((f) => f.value.trim() !== "");

  const tableContent = (
    <>
      {/* Indicador de filtros aplicados */}
      {!pagination &&
        (filters.some((f) => f.value.trim() !== "") ||
          globalSearch.trim() !== "") && (
          <div className="px-4 py-2 bg-muted/30 border-b border-border text-xs text-muted-foreground">
            Exibindo {paginatedData.length} de {sortedData.length} registros
            {(filters.some((f) => f.value.trim() !== "") ||
              globalSearch.trim() !== "") &&
              ` (${[
                globalSearch.trim() !== "" ? "busca" : null,
                filters.filter((f) => f.value.trim() !== "").length > 0
                  ? `${filters.filter((f) => f.value.trim() !== "").length} filtro${filters.filter((f) => f.value.trim() !== "").length > 1 ? "s" : ""}`
                  : null,
              ]
                .filter(Boolean)
                .join(" + ")})`}
          </div>
        )}
      <div className="relative">
        {/* Com linhas na tela, elas ficam por baixo do selo (evita "pisca-pisca"). Sem linhas,
            o aviso vai dentro do corpo da tabela — o véu, sozinho, cobria só o cabeçalho. */}
        {carregandoComLinhas && <AtualizandoConteudo />}

        <div
          className={`overflow-x-auto transition-opacity ${carregandoComLinhas ? "opacity-60" : "opacity-100"}`}
        >
          <div className="inline-block min-w-full">
            <table className="w-full text-sm table-auto md:table-fixed">
              <colgroup>
                {visibleColumnsList.map((column, index) => (
                  <col
                    key={column.key}
                    style={{
                      width: resolvedColumnsMeta[column.key]?.width,
                      minWidth: resolvedColumnsMeta[column.key]?.minWidth,
                      maxWidth: resolvedColumnsMeta[column.key]?.maxWidth,
                    }}
                  />
                ))}
              </colgroup>
              <thead className="bg-muted text-xs">
                <tr>
                  {visibleColumnsList.map((column, index) => {
                    const alignment =
                      resolvedColumnsMeta[column.key]?.align ?? "left";
                    const isFirstColumn = index === 0;
                    const isDelta = isDeltaColumn(column);
                    const nextIsDelta =
                      index < visibleColumnsList.length - 1 &&
                      isDeltaColumn(visibleColumnsList[index + 1]);
                    const cellPadding = isFirstColumn
                      ? "pl-3 pr-2"
                      : isDelta
                        ? "pl-4 pr-2"
                        : nextIsDelta
                          ? "pl-2.5 pr-1"
                          : "px-2.5";
                    return (
                      <th
                        key={column.key}
                        className={`${cellPadding} py-2 ${
                          alignment === "right"
                            ? "text-right"
                            : alignment === "center"
                              ? "text-center"
                              : "text-left"
                        } ${
                          column.sortable !== false
                            ? "cursor-pointer hover:bg-muted/80 transition-colors"
                            : ""
                        } ${
                          sortConfig?.key === column.key
                            ? "underline decoration-2 underline-offset-2"
                            : ""
                        } whitespace-nowrap overflow-hidden`}
                        onClick={() => handleSort(column.key)}
                      >
                        <div
                          className={`flex items-center min-w-0 ${
                            alignment === "right"
                              ? "justify-end"
                              : alignment === "center"
                                ? "justify-center"
                                : "justify-start"
                          }`}
                        >
                          {(() => {
                            const displayText = toUcWords(column.header);
                            const responsiveStyles =
                              generateResponsiveFontStyles(displayText);
                            return (
                              <TruncatedTextWithTooltip text={displayText}>
                                {({ ref, isTruncated }) => (
                                  <span
                                    ref={
                                      ref as React.RefObject<HTMLSpanElement>
                                    }
                                    style={responsiveStyles}
                                    className="flex items-center min-w-0"
                                  >
                                    {displayText}
                                    {getSortIcon(column.key)}
                                  </span>
                                )}
                              </TruncatedTextWithTooltip>
                            );
                          })()}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="text-xs">
                {semLinhas && (loading || vazioConfirmado) && (
                  <tr data-sem-linhas={loading ? "carregando" : "vazio"}>
                    <td colSpan={visibleColumnsList.length}>
                      {loading ? (
                        <CarregandoConteudo />
                      ) : (
                        <div className="py-10 text-center text-sm text-muted-foreground">
                          {data.length > 0 || buscaAtiva
                            ? "Nenhum registro corresponde à busca."
                            : mensagemVazia}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                {paginatedData.map((item, index) => {
                  // Verificar se a linha está selecionada
                  const isRowSelected =
                    typeof selectedRowIndex === "function"
                      ? selectedRowIndex(item, index)
                      : selectedRowIndex === index;

                  // A linha é clicável se houver onRowClick ou clickableRows
                  const isRowClickable = !!(onRowClick || clickableRows);

                  return (
                    <tr
                      key={index}
                      className={`border-b border-border hover:bg-muted/50 ${
                        index % 2 === 0 ? "bg-muted/20" : "bg-background"
                      } ${isRowSelected ? "!bg-primary/10 ring-1 ring-inset ring-primary/30" : ""} ${
                        isRowClickable ? "cursor-pointer" : ""
                      }`}
                      onClick={
                        onRowClick
                          ? (e) => onRowClick(item, index, e)
                          : undefined
                      }
                      data-selected={isRowSelected ? "true" : undefined}
                    >
                      {visibleColumnsList.map((column, columnIndex) => {
                        const cellValue = item[column.key];
                        const heatmapClasses = getHeatmapClasses(
                          typeof cellValue === "number"
                            ? cellValue
                            : parseFloat(cellValue) || 0,
                          column.key,
                        );
                        const alignment =
                          resolvedColumnsMeta[column.key]?.align ?? "left";
                        const isFirstColumn = columnIndex === 0;
                        const isDelta = isDeltaColumn(column);
                        const nextIsDelta =
                          columnIndex < visibleColumnsList.length - 1 &&
                          isDeltaColumn(visibleColumnsList[columnIndex + 1]);
                        const cellPadding = isFirstColumn
                          ? "pl-3 pr-2"
                          : isDelta
                            ? "pl-4 pr-2"
                            : nextIsDelta
                              ? "pl-2.5 pr-1"
                              : "px-2.5";

                        const titleValue = column.render
                          ? undefined
                          : cellValue === null || cellValue === undefined
                            ? ""
                            : String(cellValue);

                        return (
                          <td
                            key={column.key}
                            className={`${cellPadding} py-2 ${
                              alignment === "right"
                                ? "text-right"
                                : alignment === "center"
                                  ? "text-center"
                                  : "text-left"
                            } ${heatmapClasses} whitespace-nowrap`}
                          >
                            <div
                              className={`flex items-center min-w-0 ${
                                alignment === "right"
                                  ? "justify-end"
                                  : alignment === "center"
                                    ? "justify-center"
                                    : "justify-start"
                              }`}
                            >
                              {column.render ? (
                                column.render(cellValue, item, index)
                              ) : (
                                <span
                                  className="block truncate"
                                  title={titleValue}
                                >
                                  {String(cellValue ?? "")}
                                </span>
                              )}
                              {renderDeltaIcon(column, item)}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {effectiveTotal && !(loading && semLinhas) && (
                  <tr className="border-t-2 border-border bg-muted/50 font-semibold">
                    {visibleColumnsList.map((column, columnIndex) => {
                      const alignment =
                        resolvedColumnsMeta[column.key]?.align ?? "left";
                      const isFirstColumn = columnIndex === 0;
                      const isDelta = isDeltaColumn(column);
                      const nextIsDelta =
                        columnIndex < visibleColumnsList.length - 1 &&
                        isDeltaColumn(visibleColumnsList[columnIndex + 1]);
                      const cellPadding = isFirstColumn
                        ? "pl-3 pr-2"
                        : isDelta
                          ? "pl-4 pr-2"
                          : nextIsDelta
                            ? "pl-2.5 pr-1"
                            : "px-2.5";
                      return (
                        <td
                          key={column.key}
                          className={`${cellPadding} py-2 ${
                            alignment === "right"
                              ? "text-right"
                              : alignment === "center"
                                ? "text-center"
                                : "text-left"
                          } whitespace-nowrap`}
                        >
                          <div
                            className={`flex items-center min-w-0 ${
                              alignment === "right"
                                ? "justify-end"
                                : alignment === "center"
                                  ? "justify-center"
                                  : "justify-start"
                            }`}
                          >
                            {column.render ? (
                              column.render(
                                effectiveTotal[column.key],
                                effectiveTotal,
                                -1,
                                true,
                              )
                            ) : (
                              <span
                                className="block truncate"
                                title={String(effectiveTotal[column.key] ?? "")}
                              >
                                {String(effectiveTotal[column.key] ?? "")}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                )}
              </tbody>
            </table>
            {showPagination && !(loading && semLinhas) && (
              <div className="px-3 py-2 text-center text-muted-foreground text-xs bg-muted/30 border-t border-border">
                <div className="mb-1 flex items-center justify-between gap-3">
                  <div className="text-left">
                    {(() => {
                      if (totalCount <= 0) return "Exibindo 0 registros de 0";
                      const start = Math.min(
                        (currentPage - 1) * pageSize + 1,
                        totalCount,
                      );
                      const end = Math.min(
                        start + Math.max(0, paginatedData.length - 1),
                        totalCount,
                      );
                      return `Exibindo ${start} a ${end} de ${totalCount} registros`;
                    })()}
                  </div>
                  <div className="text-right">
                    Página {currentPage} de {totalPages}
                  </div>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <button
                    className="px-3 py-1.5 text-sm font-medium rounded-md bg-background border border-border hover:bg-muted hover:border-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    disabled={currentPage <= 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    ‹ Anterior
                  </button>
                  <button
                    className="px-3 py-1.5 text-sm font-medium rounded-md bg-background border border-border hover:bg-muted hover:border-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Próxima ›
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  const cardBody = fillRemainingSpaceClassName ? (
    <div className="flex min-h-0 flex-1 flex-col">
      {tableContent}
      <div className={`flex-1 ${fillRemainingSpaceClassName}`} />
    </div>
  ) : (
    tableContent
  );

  if (variant === "plain") {
    return (
      <div
        className={`${fillRemainingSpaceClassName ? "flex h-full flex-col " : ""}${className} overflow-visible`}
      >
        {header}
        {cardBody}
      </div>
    );
  }

  return (
    <Card
      className={`hover:shadow-md transition-shadow ${fillRemainingSpaceClassName ? "flex h-full flex-col" : "h-fit"} ${className} overflow-visible`}
    >
      {header}
      <CardContent
        className={`${fillRemainingSpaceClassName ? "flex min-h-0 flex-1 flex-col" : ""} p-0`}
      >
        {cardBody}
      </CardContent>
    </Card>
  );
};
